// Pure helpers for deriving request status and approval-related text.
// Extracted from RequestDetail.jsx so they can be unit-tested in isolation
// and reused by other call sites (e.g. portal.jsx, AdminPages.jsx) without
// dragging the 11k-line component along.

import type { ChildRow, RequestView } from "../types/database.js";
import type { ApprovalStatus, RequestStatus } from "../utils/enums";
import { APPROVAL_STATUSES, REQUEST_STATUSES } from "../utils/enums";
import { calcUniItems } from "../utils/formConstants";
import { formatRef, money } from "../utils/helpers";

// ── Local helper types ────────────────────────────────────────────────────────

/** Per-child approval decisions passed to applyApprovalStatuses. */
interface ChildApprovalDecision {
  fees?: ApprovalStatus[];
  uniforms?: ApprovalStatus;
  shoes?: ApprovalStatus;
  laptop?: ApprovalStatus;
}
type ApprovalDecisions = Record<number, ChildApprovalDecision>;

/** Per-child snapshot produced by buildPostSaveSnapshot. */
interface ChildSnapshot {
  fees: ApprovalStatus[];
  uniforms: ApprovalStatus | null;
  shoes: ApprovalStatus | null;
  laptop: ApprovalStatus | null;
}
export type ApprovalsSnapshot = Record<number, ChildSnapshot>;

/** A comment template row from the DB (or TEMPLATE_SEED). */
export interface CommentTemplate {
  id: string;
  text?: string;
  appends_next_steps?: string;
  appendsNextSteps?: string;
}

/** Match criteria inside a NextStepsTemplate. */
interface NextStepsMatchCriteria {
  feeType?: string;
  state?: string;
  supplier?: string;
  supplierType?: string;
}

/** A next-steps template entry (from NEXT_STEPS_DEFAULTS or the DB blob). */
export interface NextStepsTemplate {
  id: string;
  category: string;
  match: NextStepsMatchCriteria;
  title: string;
  text: string;
}

/** The item descriptor passed to getNextStepsText. */
export interface NextStepsItem {
  category: string;
  feeType?: string;
  state?: string;
  supplier?: string;
  supplierType?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Decide the overall status of a request from its children's per-item
 * approval statuses.
 *
 * D-1 (26 June 2026) — the old all-Approved (`Approved`) and all-Declined
 * (`Declined`) terminal results, AND the previously-`Part Processed`
 * fully-decided MIX (some approved, some declined, none Pending/Needs Info),
 * all collapse into the single terminal `Fully Processed`. "Fully Processed"
 * means every item has a final yes/no — whatever the approve/decline mix.
 * `Needs Info` counts as NOT-yet-final (like Pending), so it keeps a request
 * in `Part Processed`. (The overall status never says "Approval"/"Approved" —
 * that wording is item-level only; "Partial approval" was renamed to
 * "Part Processed" on 26 June 2026.)
 *
 *   • Fully Processed   — every item is finally decided (Approved or Declined);
 *                         no Pending, no Needs Info
 *   • Submitted         — every item is still "Pending" (untouched / fresh)
 *   • Part Processed    — some items decided but at least one still
 *                         Pending or Needs Info
 *   • null              — the request has no items at all
 */
export function deriveOverallStatus(req: Pick<RequestView, "children">): RequestStatus | null {
  const allEntries: string[] = [];
  (req.children || []).forEach(function (child) {
    (child.fees || []).forEach(function (f) {
      allEntries.push(f.approvalStatus || APPROVAL_STATUSES.PENDING);
    });
    if (child.uniforms) allEntries.push(child.uniforms.approvalStatus || APPROVAL_STATUSES.PENDING);
    if (child.shoes) allEntries.push(child.shoes.approvalStatus || APPROVAL_STATUSES.PENDING);
    if (child.laptop) allEntries.push(child.laptop.approvalStatus || APPROVAL_STATUSES.PENDING);
  });
  if (allEntries.length === 0) return null;

  const allPending = allEntries.every((s) => s === APPROVAL_STATUSES.PENDING);
  if (allPending) return REQUEST_STATUSES.SUBMITTED; // fresh / untouched

  // "Undecided" = still Pending OR flagged Needs Info (awaiting information).
  const anyUndecided = allEntries.some(
    (s) => s === APPROVAL_STATUSES.PENDING || s === APPROVAL_STATUSES.NEEDS_INFO
  );
  // Every item has a final Approve/Decline → the request is fully processed.
  if (!anyUndecided) return REQUEST_STATUSES.FULLY_PROCESSED;
  return REQUEST_STATUSES.PART_PROCESSED;
}

/**
 * Apply per-item approval decisions to a request, returning a NEW request
 * object with each item's `approvalStatus` set. The input is not mutated.
 *
 * The `approvals` argument is keyed by child index. Each entry can include:
 *   • `fees` — array of status strings, indexed to match `child.fees`
 *   • `uniforms`, `shoes`, `laptop` — status strings for the single items
 *
 * Missing decisions default to "Pending". Items that don't exist on a child
 * (e.g. `child.uniforms == null`) are left absent — no synthetic record is
 * created.
 */
export function applyApprovalStatuses(
  req: RequestView,
  approvals?: ApprovalDecisions | null
): RequestView {
  const safeApprovals = approvals || {};
  return {
    ...req,
    children: (req.children || []).map(function (child, ci) {
      const childApp = safeApprovals[ci] || {};
      const updated: ChildRow = { ...child };
      updated.fees = (child.fees || []).map(function (f, fi) {
        return { ...f, approvalStatus: childApp.fees?.[fi] || APPROVAL_STATUSES.PENDING };
      });
      if (child.uniforms) {
        updated.uniforms = {
          ...child.uniforms,
          approvalStatus: childApp.uniforms || APPROVAL_STATUSES.PENDING,
        };
      }
      if (child.shoes) {
        updated.shoes = {
          ...child.shoes,
          approvalStatus: childApp.shoes || APPROVAL_STATUSES.PENDING,
        };
      }
      if (child.laptop) {
        updated.laptop = {
          ...child.laptop,
          approvalStatus: childApp.laptop || APPROVAL_STATUSES.PENDING,
        };
      }
      return updated;
    }),
  };
}

/** A decision has been made on an item — Approved, Declined or Needs Info (anything but Pending/empty). */
export function isDecided(status?: string | null): boolean {
  return !!status && status !== APPROVAL_STATUSES.PENDING;
}

/**
 * Is this request a CONFIRMED, terminal `Fully Processed` request — i.e. locked
 * (D-2/D-4: once fully processed, nobody reopens/reverses it, for now)?
 *
 * The lock is keyed on CONFIRMATION (`approvalsConfirmedAt`), NOT merely on the
 * derived `Fully Processed` status. During pre-confirm staging the in-memory
 * `req.status` can already read `Fully Processed` (every item decided) while
 * `approvalsConfirmedAt` is still null — that request is NOT locked: the
 * responder may still revert/adjust until "Confirm & Send" (the ephemeral-staging
 * principle). Only once Confirm & Send stamps `approvalsConfirmedAt` is the
 * request truly terminal. This mirrors the db-8 DB guard (the persisted status
 * only ever reaches `Fully Processed` via Confirm & Send, which sets
 * `approvals_confirmed_at` in the same write).
 */
export function isLockedFullyProcessed(
  req?: { status?: string | null; approvalsConfirmedAt?: string | null } | null
): boolean {
  return (
    !!req && req.status === REQUEST_STATUSES.FULLY_PROCESSED && req.approvalsConfirmedAt != null
  );
}

interface DecidableItem {
  approvalStatus?: string;
  confirmedAt?: string;
  reason?: unknown;
  reasonCategory?: unknown;
  [key: string]: unknown;
}

/**
 * Set an item's `approvalStatus`. If the decision actually CHANGED, drop any prior
 * `confirmedAt` stamp so the item re-shows as "New" and must be re-confirmed.
 * Returns a NEW item; the input is not mutated.
 *
 * (Whether a responder/admin may reverse a confirmed decision at all is an open
 *  David + John policy decision; this keeps the "New" flag correct if/when it's allowed.)
 */
export function applyItemDecision(item: DecidableItem, newStatus?: string | null): DecidableItem {
  const status = newStatus || APPROVAL_STATUSES.PENDING;
  // The ephemeral Declined/Needs-Info reason is only meaningful while the item is
  // on one of those statuses; drop it the moment the decision moves off them.
  const keepsReason =
    status === APPROVAL_STATUSES.DECLINED || status === APPROVAL_STATUSES.NEEDS_INFO;
  const reason = keepsReason ? item.reason : undefined;
  // The Reason type (db-207 decision log) follows the reason exactly.
  const reasonCategory = keepsReason ? item.reasonCategory : undefined;
  if (item.approvalStatus === status) {
    return { ...item, approvalStatus: status, reason, reasonCategory };
  }
  return { ...item, approvalStatus: status, reason, reasonCategory, confirmedAt: undefined };
}

/**
 * Stamp `confirmedAt` on every DECIDED item (Approved / Declined / Needs Info)
 * that doesn't already have a `confirmedAt` value. Returns a NEW request —
 * the input is not mutated.
 *
 * Items already stamped in a previous session keep their original timestamp,
 * which lets the UI tell apart "approved in this session" from "approved
 * previously" when building summaries.
 *
 * Items that don't exist on a child (uniforms / shoes / laptop absent) are
 * left absent.
 */
export function stampConfirmedAt(req: RequestView, confirmedAt: string): RequestView {
  return {
    ...req,
    children: (req.children || []).map(function (child) {
      const updated: ChildRow = { ...child };
      if (child.fees) {
        updated.fees = child.fees.map(function (f) {
          if (isDecided(f.approvalStatus) && !f.confirmedAt) {
            return { ...f, confirmedAt };
          }
          return f;
        });
      }
      if (
        child.uniforms &&
        isDecided(child.uniforms.approvalStatus) &&
        !child.uniforms.confirmedAt
      ) {
        updated.uniforms = { ...child.uniforms, confirmedAt };
      }
      if (child.shoes && isDecided(child.shoes.approvalStatus) && !child.shoes.confirmedAt) {
        updated.shoes = { ...child.shoes, confirmedAt };
      }
      if (child.laptop && isDecided(child.laptop.approvalStatus) && !child.laptop.confirmedAt) {
        updated.laptop = { ...child.laptop, confirmedAt };
      }
      return updated;
    }),
  };
}

/**
 * Build a snapshot of the CURRENT approvalStatus for every item, keyed by
 * child index. Used after a successful confirm to re-baseline the in-memory
 * refs (`initialApprovalsRef`, `confirmedSnapshotRef`,
 * `sessionStartApprovalsRef`) so the next session knows what was already
 * decided.
 *
 * Always returns an object — never null. Absent items (e.g. `child.uniforms == null`)
 * render as `null` rather than a status string, so consumers can distinguish
 * "item not present" from "item pending".
 */
export function buildPostSaveSnapshot(req: Pick<RequestView, "children">): ApprovalsSnapshot {
  const snapshot: ApprovalsSnapshot = {};
  (req.children || []).forEach(function (child, ci) {
    snapshot[ci] = {
      fees: (child.fees || []).map(function (f) {
        return (f.approvalStatus || APPROVAL_STATUSES.PENDING) as ApprovalStatus;
      }),
      uniforms: child.uniforms
        ? ((child.uniforms.approvalStatus || APPROVAL_STATUSES.PENDING) as ApprovalStatus)
        : null,
      shoes: child.shoes
        ? ((child.shoes.approvalStatus || APPROVAL_STATUSES.PENDING) as ApprovalStatus)
        : null,
      laptop: child.laptop
        ? ((child.laptop.approvalStatus || APPROVAL_STATUSES.PENDING) as ApprovalStatus)
        : null,
    };
  });
  return snapshot;
}

/**
 * Build the human-readable summary comment that gets staged into the
 * request's notes when an approver confirms decisions. The text follows
 * this structure:
 *
 *   <greeting with refNo>
 *
 *   [<approved header>
 *    • Child N — items...]
 *
 *   [<declined header>
 *    • Child N — items...]
 *
 *   [<pending header>
 *    • Child N — items...]
 *
 *   <closing>
 *
 *   <signoff>
 *
 * Each item-list section is only included when its list is non-empty.
 * Header / greeting / closing / signoff wording is template-driven via
 * `allTemplates` rows (ids `_sum_greeting`, `_sum_approved`, `_sum_declined`,
 * `_sum_pending`, `_sum_closing`, `_sum_signoff`), so staff can edit copy
 * without a deploy; built-in defaults are used when a template is missing.
 *
 * "Newly confirmed" items are those approved in THIS session — not carried
 * over from a previous one. "Newly declined" items are those whose status
 * changed to Declined since session start (so the shelter only sees what
 * was decided in this round).
 *
 * Pending and "Needs Info" items always appear in the pending list (the
 * shelter needs to see they're still outstanding) regardless of prior state.
 *
 * @param updatedReq                  Request after applyApprovalStatuses + stampConfirmedAt
 * @param sessionStartApprovals       Snapshot of approvalStatuses at session start (from sessionStartApprovalsRef.current); used to detect "newly declined"
 * @param prevConfirmedAt             Previous approvalsConfirmedAt timestamp (ISO), or null/undefined on first confirm
 * @param confirmedAt                 Timestamp for THIS confirm (ISO); cutoff for "newly confirmed"
 * @param allTemplates                Array of comment-template rows (`{ id, text }`)
 * @param refNoFallback               Used as refNo when `updatedReq.refNo` is missing
 * @returns                           The assembled summary text (string)
 */
export function buildApprovalSummaryText({
  updatedReq,
  sessionStartApprovals,
  prevConfirmedAt,
  confirmedAt,
  allTemplates,
  refNoFallback,
}: {
  // Frontend-mapped request (camelCase `refNo` + mapped `children`), NOT the raw
  // snake_case RequestView — this summary runs on the already-mapped req object.
  updatedReq: { refNo?: string | null; children?: ChildRow[] };
  sessionStartApprovals?: ApprovalsSnapshot | null;
  prevConfirmedAt?: string | null;
  confirmedAt: string;
  allTemplates?: CommentTemplate[];
  refNoFallback?: string | null;
}): string {
  function isNewlyConfirmed(
    item: { approvalStatus?: string; confirmedAt?: string | null } | null | undefined
  ): boolean {
    if (!item || item.approvalStatus !== APPROVAL_STATUSES.APPROVED) return false;
    if (!prevConfirmedAt) {
      // First confirm (or after manager reset): "newly confirmed" =
      // items stamped with THIS session's confirmedAt. Items carrying over
      // from earlier still have their older confirmedAt and won't match.
      return item.confirmedAt === confirmedAt;
    }
    if (!item.confirmedAt) return true; // approved but not yet stamped
    return new Date(item.confirmedAt) > new Date(prevConfirmedAt);
  }

  function isNewlyDeclined(
    currentStatus: string | undefined,
    origStatus: string | undefined
  ): boolean {
    return (
      currentStatus === APPROVAL_STATUSES.DECLINED && origStatus !== APPROVAL_STATUSES.DECLINED
    );
  }

  function tplText(id: string, fallback: string): string {
    const t = (allTemplates || []).find(function (x) {
      return x.id === id;
    });
    return t?.text ? t.text : fallback;
  }

  // db-79: when a responder approved a DIFFERENT figure than the DFV Service requested,
  // append the original — e.g. "$300 (requested $400)". Only annotates when a requested
  // figure was recorded (post-db-79) and it differs from the approved amount.
  function reqNote(approved: unknown, requested: unknown): string {
    const a = Number.parseFloat(String(approved ?? ""));
    const r = Number.parseFloat(String(requested ?? ""));
    if (!Number.isFinite(a) || !Number.isFinite(r)) return "";
    if (Math.round(a * 100) === Math.round(r * 100)) return "";
    return ` (requested ${money(r)})`;
  }

  // The QLD "Stationery" fee is a dispatched physical Stationery Pack (like a
  // Device): its $ figure is an ESTIMATE that a picker later replaces with the
  // actual cost. So in the APPROVED list it reads "Stationery Pack" and its amount
  // is marked "(Estimated)". Display-only — the stored feeType stays "Stationery".
  // Scope is the APPROVED section ONLY (John, 24 Aug): the declined / under-review
  // lists keep plain "Stationery" (so "Stationery, Textbooks" is unchanged).
  // "Stationery & Textbooks" is a separate reimbursement fee, never included.
  function displayFeeType(label: string): string {
    return label === "Stationery" ? "Stationery Pack" : label;
  }
  function isEstimatedFee(feeType?: string | null): boolean {
    return feeType === "Stationery";
  }

  const approvedLines: string[] = [];
  const declinedLines: string[] = [];
  const pendingLines: string[] = [];

  // The responder's Declined / Needs-Info reason lives on the in-memory item as
  // `reason` (ephemeral — never a DB column; see ResponderChildView). Read it safely.
  function reasonOf(item: unknown): string {
    return String((item as { reason?: string } | null)?.reason || "").trim();
  }

  // Combine a child's declined / under-review items into text, collapsing runs of
  // items that share the same reason so a wholesale category decline reads once:
  //   "School Costs, Textbooks: <reason>; Uniforms: <other reason>"
  function groupByReason(entries: Array<{ label: string; reason: string }>): string {
    const groups: Array<{ reason: string; labels: string[] }> = [];
    entries.forEach(function (e) {
      const last = groups[groups.length - 1];
      if (last && last.reason === e.reason) last.labels.push(e.label);
      else groups.push({ reason: e.reason, labels: [e.label] });
    });
    // Prefix the responder's reason with "REASON: " (John, 8 Aug) so a declined /
    // more-info line reads e.g. "Uniforms: REASON: $100 per skirt is too expensive".
    return groups
      .map((g) => g.labels.join(", ") + (g.reason ? `: REASON: ${g.reason}` : ""))
      .join("; ");
  }

  (updatedReq.children || []).forEach(function (child, ci) {
    const childDesc =
      "Child " +
      (ci + 1) +
      (child.gender ? ` (${child.gender}${child.yearLevel ? ` · ${child.yearLevel}` : ""})` : "") +
      (child.suburb ? ` — ${child.suburb}` : "");
    const approvedItems: string[] = [];
    const declinedItems: Array<{ label: string; reason: string }> = [];
    const pendingItems: Array<{ label: string; reason: string }> = [];
    const origSnap: Partial<ChildSnapshot> = sessionStartApprovals?.[ci] || {};

    (child.fees || []).forEach(function (f, fi) {
      const origFeeStatus = origSnap.fees?.[fi] || APPROVAL_STATUSES.PENDING;
      if (isNewlyConfirmed(f)) {
        const estSuffix = isEstimatedFee(f.feeType) ? " (Estimated)" : "";
        approvedItems.push(
          displayFeeType(f.feeType || "Fees") +
            (f.feeAmount ? ` ${money(f.feeAmount)}${estSuffix}` : "") +
            reqNote(f.feeAmount, f.requestedAmount)
        );
      } else if (isNewlyDeclined(f.approvalStatus, origFeeStatus)) {
        declinedItems.push({ label: f.feeType || "School Costs", reason: reasonOf(f) });
      } else if (
        f.approvalStatus === APPROVAL_STATUSES.PENDING ||
        f.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
      ) {
        pendingItems.push({
          label: f.feeType || "School Costs",
          reason: f.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO ? reasonOf(f) : "",
        });
      }
    });

    if (child.uniforms) {
      if (isNewlyConfirmed(child.uniforms)) {
        const uAmt = calcUniItems([child.uniforms]);
        approvedItems.push(
          `Uniforms${uAmt > 0 ? ` ${money(uAmt)}` : ""}${reqNote(uAmt, child.uniforms.requestedAmount)}`
        );
      } else if (isNewlyDeclined(child.uniforms.approvalStatus, origSnap.uniforms ?? undefined)) {
        declinedItems.push({ label: "Uniforms", reason: reasonOf(child.uniforms) });
      } else if (
        child.uniforms.approvalStatus === APPROVAL_STATUSES.PENDING ||
        child.uniforms.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
      ) {
        pendingItems.push({
          label: "Uniforms",
          reason:
            child.uniforms.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
              ? reasonOf(child.uniforms)
              : "",
        });
      }
    }

    if (child.shoes) {
      if (isNewlyConfirmed(child.shoes)) {
        const sAmt =
          (child.shoes.jQty || 0) * (parseFloat(String(child.shoes.jCost)) || 0) +
          (child.shoes.lQty || 0) * (parseFloat(String(child.shoes.lCost)) || 0);
        approvedItems.push(
          `Shoes${sAmt > 0 ? ` ${money(sAmt)}` : ""}${reqNote(sAmt, child.shoes.requestedAmount)}`
        );
      } else if (isNewlyDeclined(child.shoes.approvalStatus, origSnap.shoes ?? undefined)) {
        declinedItems.push({ label: "Shoes", reason: reasonOf(child.shoes) });
      } else if (
        child.shoes.approvalStatus === APPROVAL_STATUSES.PENDING ||
        child.shoes.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
      ) {
        pendingItems.push({
          label: "Shoes",
          reason:
            child.shoes.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
              ? reasonOf(child.shoes)
              : "",
        });
      }
    }

    if (child.laptop) {
      if (isNewlyConfirmed(child.laptop)) {
        approvedItems.push(
          "Device" +
            (child.laptop.estimatedCost
              ? ` ${money(child.laptop.estimatedCost)} (Estimated)`
              : "") +
            reqNote(child.laptop.estimatedCost, child.laptop.requestedAmount)
        );
      } else if (isNewlyDeclined(child.laptop.approvalStatus, origSnap.laptop ?? undefined)) {
        declinedItems.push({ label: "Device", reason: reasonOf(child.laptop) });
      } else if (
        child.laptop.approvalStatus === APPROVAL_STATUSES.PENDING ||
        child.laptop.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
      ) {
        pendingItems.push({
          label: "Device",
          reason:
            child.laptop.approvalStatus === APPROVAL_STATUSES.NEEDS_INFO
              ? reasonOf(child.laptop)
              : "",
        });
      }
    }

    if (approvedItems.length > 0) {
      approvedLines.push(`• ${childDesc} — ${approvedItems.join(", ")}`);
    }
    if (declinedItems.length > 0) {
      declinedLines.push(`• ${childDesc} — ${groupByReason(declinedItems)}`);
    }
    if (pendingItems.length > 0) {
      pendingLines.push(`• ${childDesc} — ${groupByReason(pendingItems)}`);
    }
  });

  const refNum = formatRef(updatedReq.refNo || refNoFallback);
  const greeting = tplText(
    "_sum_greeting",
    "Thank you for your Request {{refNo}} with the following outcomes:"
  )
    .split("{{refNo}}")
    // refNum is string in practice (refNoFallback covers the empty case); cast
    // keeps the existing runtime while satisfying Array.join's string param.
    .join(refNum as string);
  const hApproved = tplText(
    "_sum_approved",
    "The following items have been APPROVED in this session:"
  );
  const hDeclined = tplText(
    "_sum_declined",
    "The following items have been DECLINED in this session:"
  );
  const hPending = tplText("_sum_pending", "The following items are still UNDER REVIEW:");
  const closing = tplText(
    "_sum_closing",
    "When you go back into the Request, there will be instructions to print for each Child on how to proceed for items approved."
  );
  const signoff = tplText("_sum_signoff", "Kind regards\nZephyr Education");

  let summaryText = `${greeting}\n\n`;
  if (approvedLines.length > 0) summaryText += `${hApproved}\n${approvedLines.join("\n")}\n\n`;
  if (declinedLines.length > 0) summaryText += `${hDeclined}\n${declinedLines.join("\n")}\n\n`;
  if (pendingLines.length > 0) summaryText += `${hPending}\n${pendingLines.join("\n")}\n\n`;
  summaryText += `${closing}\n\n${signoff}`;

  return summaryText;
}

// ────────────────────────────────────────────────────────────────────────
// Default "next steps" templates — fallback when DB-backed templates
// aren't available. Each entry has match criteria and free-text body.
// ────────────────────────────────────────────────────────────────────────
export const NEXT_STEPS_DEFAULTS: NextStepsTemplate[] = [
  {
    id: "stationery_qld",
    category: "fees",
    match: { feeType: "Stationery" },
    title: "Stationery (All States)",
    text: "The stationery pack request has been forwarded to our fulfilment team for dispatch to your attention. You should receive the items in the coming days. There is no further action required on your part.",
  },
  {
    id: "textbooks_qld",
    category: "fees",
    match: { feeType: "Textbooks", state: "QLD" },
    title: "Textbooks (QLD)",
    text: "Your organisation will cover the costs and seek reimbursement from Zephyr.\n\nFOR REIMBURSEMENT YOU WILL NEED TO PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nUpload via Files & Invoices tab (Invoices section).\n\nPayment processed within 1–2 business days of receiving all documents.\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "stationery_textbooks_other",
    category: "fees",
    match: { feeType: "Stationery & Textbooks" },
    title: "Stationery & Textbooks (Other States)",
    text: "Please note that voluntary contribution fees are not eligible. Where included in a request, these amounts have been excluded from the total approved.\n\nYour organisation will cover the costs and seek reimbursement from Zephyr.\n\nFOR REIMBURSEMENT YOU WILL NEED TO PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nUpload via Files & Invoices tab (Invoices section).\n\nPayment processed within 1–2 business days of receiving all documents.\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "camp_fees",
    category: "fees",
    match: { feeType: "Camp Fees" },
    title: "Camp Fees",
    text: "Please arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "resource_fees",
    category: "fees",
    match: { feeType: "Resource Fees" },
    title: "Resource Fees",
    text: "NOTE: we may have reduced fees as Zephyr's policy is to pay for one (current) term only, and if this is the case, you will need to have the school provide an invoice for the one (current) term as approved.\n\nPlease arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "device_hire",
    category: "fees",
    match: { feeType: "Device Hire" },
    title: "Device Hire",
    text: "Zephyr's policy is to fund this on a term-by-term basis, so we'll just need an invoice for the current term's device hire.\n\nPlease arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "school_activities",
    category: "fees",
    match: { feeType: "School Activities" },
    title: "School Activities",
    text: "Please arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "non_school_activities",
    category: "fees",
    match: { feeType: "Non-school Activities" },
    title: "Non-school Activities",
    text: "Please arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "swim_gear",
    category: "fees",
    match: { feeType: "Swim Gear" },
    title: "Swim Gear",
    text: "Please arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nPAYMENT OPTIONS:\nZephyr no longer processes credit card payments. If your organisation can purchase the items, we'll reimburse within 1–2 business days. If not, please let us know promptly.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "other_fees",
    category: "fees",
    match: { feeType: "Other (specify)" },
    title: "Other",
    text: "Please arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n\nYOUR ACTIONS:\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nPAYMENT OPTIONS:\nZephyr no longer processes credit card payments. If your organisation can purchase the items, we'll reimburse within 1–2 business days. If not, please let us know promptly.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "uniforms_school_locker",
    category: "uniforms",
    match: { supplier: "School Locker" },
    title: "Uniforms — School Locker",
    text: "The approved order form has been sent to School Locker, who will contact you to arrange collection.\n\nWhen we receive School Locker's Reference (OSD-[NUMBER]), this will be recorded in the School Locker Uniform tab and enable you to print the Order. This number is normally available within 1 hour.\n\nYOUR ACTION:\n• Print the order using the Print Order button next to the uniform items\n• Give printout to your client to take to School Locker for pickup\n\nSchool Locker will invoice Zephyr directly.",
  },
  {
    id: "uniforms_authority",
    category: "uniforms",
    match: { supplierType: "authority" },
    title: "Uniforms — Authority Doc Supplier",
    text: "YOUR ACTION:\n• Open the Uniforms tab for each child whose uniforms will be coming from {{supplier}}.\n• Here you will find a Print Authorisation for Purchase of Uniforms button.\n• Print the Authorisation\n• Manually complete Section 2\n• Give printout to your client to take to the Supplier when collecting uniforms\n\nThe Supplier will invoice Zephyr directly.\n\nIMPORTANT: The authorisation has an expiry date — please arrange collection before this date.",
  },
  {
    id: "uniforms_direct",
    category: "uniforms",
    match: { supplierType: "direct" },
    title: "Uniforms — School or Other Supplier",
    text: "YOUR ACTION:\n• Ensure that the Supplier has a copy of the approved uniforms list for each child when the uniforms are being ordered by using the PRINT button in the Uniforms section.\n• Arrange a tax-compliant invoice made out to Zephyr Education Limited quoting your Zephyr Reference Number.\n• Upload invoice via Files & Invoices tab (Invoices section), OR\n• Have supplier email to invoicing@zephyreducation.com.au with the reference number as subject\n\nPAYMENT OPTIONS:\nZephyr no longer processes credit card payments. If your organisation can purchase the items, we'll reimburse within 1–2 business days. If not, please let us know promptly.\n\nFOR REIMBURSEMENT PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "uniforms_shelter_buy",
    category: "uniforms",
    match: { supplierType: "reimbursement" },
    title: "Uniforms — DFV Service to Buy",
    text: "Ensure that the Supplier has a copy of the approved uniforms list for each child when the uniforms are being ordered by using the PRINT button in the Uniforms section.\n\nYour organisation will cover the costs and seek reimbursement from Zephyr.\n\nFOR REIMBURSEMENT YOU WILL NEED TO PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nUpload via Files & Invoices tab (Invoices section).\n\nPayment processed within 1–2 business days of receiving all documents.\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "shoes",
    category: "shoes",
    match: {},
    title: "Shoes",
    text: "Your organisation will cover the costs of the approved shoes and seek reimbursement from Zephyr.\n\nFOR REIMBURSEMENT YOU WILL NEED TO PROVIDE:\n• Tax-compliant invoice from your org (ABN, GST details, bank account)\n• Original supplier invoice/receipt\n• Proof of payment to supplier\n\nUpload via Files & Invoices tab (Invoices section).\n\nPayment processed within 1–2 business days of receiving all documents.\n\nIMPORTANT:\n• Invoices are required within 14 days of approval.\n• Requests that remain outstanding after 60 days will be cancelled and closed.",
  },
  {
    id: "device",
    category: "device",
    match: {},
    title: "Device",
    text: "The device request has been forwarded to our fulfilment team for dispatch to your attention. You should receive the item/s requested in the coming days. There is no further action required on your part.",
  },
];

// The four Action Sheet next-steps classifications a uniform supplier can map to.
export type SupplierActionSheetType = "authority" | "direct" | "reimbursement" | "school_locker";

// The subset of a supplier row deriveSupplierType needs (db-50 fields). Loosely
// typed so callers can pass a full SupplierRow / DropdownSupplier without coupling
// the services layer to the data layer.
export interface SupplierTypeInput {
  kind?: string | null;
  payment_method?: string | null;
  document_type?: string | null;
  action_sheet_override?: string | null;
}

/**
 * Derive a uniform supplier's Action Sheet next-steps type from its row (db-50),
 * NOT from its name. This is the single source of truth the wizard + Action Sheet
 * both read, so they can never drift. An explicit `action_sheet_override` wins;
 * otherwise it decomposes as payment_method x document_type:
 *   school_locker capture            -> school_locker
 *   shelter_reimbursed payment       -> reimbursement
 *   supplier_invoices + auth-to-purchase -> authority
 *   everything else (approved list)  -> direct
 */
export function deriveSupplierType(
  row: SupplierTypeInput | null | undefined
): SupplierActionSheetType {
  if (!row) return "direct";
  const override = row.action_sheet_override;
  if (
    override === "authority" ||
    override === "direct" ||
    override === "reimbursement" ||
    override === "school_locker"
  )
    return override;
  if (row.kind === "school_locker") return "school_locker";
  if (row.payment_method === "shelter_reimbursed") return "reimbursement";
  if (row.document_type === "authorisation_to_purchase") return "authority";
  return "direct";
}

/**
 * Pick the first matching next-steps template for a given approval item.
 * Falls back to NEXT_STEPS_DEFAULTS when no DB-backed templates are passed.
 *
 * Match priority: category must equal, then any of feeType/state/supplier/
 * supplierType in the template's `match` clause must match the item.
 * The `{{supplier}}` placeholder is replaced inline.
 *
 * Returns the template's `text` string, or null if no match.
 */
export function getNextStepsText(
  templates: NextStepsTemplate[] | null | undefined,
  item: NextStepsItem
): string | null {
  const t = templates || NEXT_STEPS_DEFAULTS;
  for (let i = 0; i < t.length; i++) {
    const tmpl = t[i];
    if (tmpl.category !== item.category) continue;
    const m = tmpl.match || {};
    if (m.feeType && m.feeType !== item.feeType) continue;
    if (m.state && m.state !== item.state) continue;
    if (m.supplier && m.supplier !== item.supplier) continue;
    if (m.supplierType && m.supplierType !== item.supplierType) continue;
    let text = tmpl.text;
    if (item.supplier) text = text.replace(/\{\{supplier\}\}/g, item.supplier);
    return text;
  }
  return null;
}

// ── Section-label button colour (single source of truth) ─────────────────────
// The category buttons on each child card (School Costs & Stationery, Device, …)
// are tinted by the COMBINED approval state of the items in that section. Shared
// by ResponderChildView and ShelterRequestView so the
// responder and shelter views can never drift:
//   all approved              -> solid green
//   all declined              -> solid red
//   anything undecided        -> gold
//   mix of approved+declined  -> green-to-red gradient (left to right)
export const SECTION_STATUS_BG: Record<string, string> = {
  [APPROVAL_STATUSES.APPROVED]: "#388E3C",
  [APPROVAL_STATUSES.DECLINED]: "#C62828",
  [APPROVAL_STATUSES.PENDING]: "#F9A825",
  Mixed: "linear-gradient(to right, #388E3C, #C62828)",
};

// Collapse a section's item approval statuses into one SECTION_STATUS_BG key.
export function sectionStatus(statuses: Array<string | null | undefined>): string {
  const list = (statuses || []).filter(Boolean) as string[];
  if (list.length === 0) return APPROVAL_STATUSES.PENDING;
  if (list.every((s) => s === APPROVAL_STATUSES.APPROVED)) return APPROVAL_STATUSES.APPROVED;
  if (list.every((s) => s === APPROVAL_STATUSES.DECLINED)) return APPROVAL_STATUSES.DECLINED;
  if (list.some((s) => s !== APPROVAL_STATUSES.APPROVED && s !== APPROVAL_STATUSES.DECLINED)) {
    return APPROVAL_STATUSES.PENDING;
  }
  return "Mixed";
}

// CSS background for a section-label button, from its items' approval statuses.
export function sectionButtonBackground(statuses: Array<string | null | undefined>): string {
  return SECTION_STATUS_BG[sectionStatus(statuses)];
}
